import z from "zod";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";

import { sureApiCall } from "../axiosClient/sureApi";
import { zodParseWithLog } from "../apiErrorLogs";

/**
 * Localisation contract: sure-api uses Gedmo Translatable on
 * `TransformationReference.name`. The axios client sends `Accept-Language`
 * from `i18next.language`, so the API already returns `name` localised.
 */
const TransformationReferenceApiSchema = z.object({
    id: z.number(),
    code: z.string(),
    name: z.string(),
});

type TransformationReferenceApi = z.infer<typeof TransformationReferenceApiSchema>;

export type TransformationReferenceChoice = {
    id: number;
    code: string;
    label: string;
};

/** @deprecated Prefer {@link TransformationReferenceChoice} */
export type TransformationReference = TransformationReferenceChoice;

export const useTransformationReference = () => {
    const { i18n } = useTranslation();
    const lang = i18n.language?.startsWith("fr") ? "fr" : "en";

    const query = useQuery({
        cacheTime: 0,
        staleTime: 0,
        queryKey: ["allTransformationReferences", lang],
        queryFn: async () => {
            return await sureApiCall.get("/api/transformation_references", {
                params: { itemsPerPage: 200 },
            });
        },
        select: (request) => {
            const members = request.data["hydra:member"];
            const data = zodParseWithLog(
                members,
                TransformationReferenceApiSchema.array(),
                "allTransformationReferences",
            );

            return data.map(
                (item: TransformationReferenceApi): TransformationReferenceChoice => ({
                    id: item.id,
                    code: item.code,
                    label: item.name.trim() !== "" ? item.name : item.code,
                }),
            );
        },
    });

    return {
        transformationReference: query.data,
        transformationReferences: query.data,
        ...query,
    };
};
